import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  getComplaints, createComplaint, updateComplaint, deleteComplaint, getOperators,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, MessageSquareWarning } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { formatDateTime } from "@/lib/dates";

const emptyComplaint = { id: null, title: "", description: "", about_operator_id: null };
const NONE = "__none__";
const STATUSES = ["open", "in_progress", "resolved"];
const statusBadge = {
  open: "badge-warn",
  in_progress: "bg-[hsl(var(--brand))] text-white",
  resolved: "badge-ok",
};

export default function Complaints() {
  const { t } = useT();
  const { can } = useAuth();
  const canManage = can("admin", "manager");
  const [complaints, setComplaints] = useState([]);
  const [operators, setOperators] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => setComplaints(await getComplaints());
  useEffect(() => {
    (async () => {
      await load();
      try { setOperators(await getOperators()); }
      catch (e) { console.error("Complaints: failed to load operators", e); }
    })();
  }, []);

  const filtered = complaints.filter((c) => {
    if (filter === "open") return c.status !== "resolved";
    if (filter === "resolved") return c.status === "resolved";
    return true;
  });

  const save = async (f) => {
    try {
      if (!f.title?.trim()) { toast.error(t("complaints.saveFailed")); return; }
      await createComplaint({
        title: f.title, description: f.description,
        about_operator_id: f.about_operator_id || null,
      });
      toast.success(t("complaints.created"));
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("complaints.saveFailed")); }
  };

  const setStatus = async (c, status) => {
    try { await updateComplaint(c.id, { status }); toast.success(t("complaints.updated")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("complaints.confirmDelete"))) return;
    try { await deleteComplaint(id); toast.success(t("complaints.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const fmtDate = (iso) => formatDateTime(iso, "");

  return (
    <Layout actions={
      <Button size="sm" onClick={() => setDialog({ ...emptyComplaint })} data-testid="add-complaint-button">
        <Plus className="mr-1.5 h-4 w-4" /> {t("complaints.add")}
      </Button>
    }>
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquareWarning className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("complaints.title")} ({filtered.length})
          </CardTitle>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-36" data-testid="complaint-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("complaints.filterAll")}</SelectItem>
              <SelectItem value="open">{t("complaints.filterOpen")}</SelectItem>
              <SelectItem value="resolved">{t("complaints.filterResolved")}</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="complaints-empty">
              <MessageSquareWarning className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("complaints.empty")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((c) => (
                <div key={c.id} data-testid={`complaint-row-${c.id}`} className="rounded-lg border border-border p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{c.title}</span>
                        <Badge className={statusBadge[c.status] || "badge-warn"}>{t(`complaints.${c.status === "in_progress" ? "inProgress" : c.status}`)}</Badge>
                      </div>
                      {c.description && <div className="mt-0.5 text-xs text-muted-foreground">{c.description}</div>}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>{t("complaints.filedBy", { name: c.created_by || "-" })}</span>
                        <span>· {fmtDate(c.created_at)}</span>
                        {c.about_operator_name && <span>· {t("complaints.aboutOperator", { name: c.about_operator_name })}</span>}
                        {c.status === "resolved" && c.resolved_by && <span>· {t("complaints.resolvedBy", { name: c.resolved_by })}</span>}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex shrink-0 items-center gap-2">
                        <Select value={c.status} onValueChange={(v) => setStatus(c, v)}>
                          <SelectTrigger className="h-8 w-36" data-testid={`complaint-status-${c.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`complaints.${s === "in_progress" ? "inProgress" : s}`)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`complaint-delete-${c.id}`}>
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ComplaintDialog
        key={dialog ? (dialog.id ?? "new") : "closed"}
        dialog={dialog}
        operators={operators}
        onClose={() => setDialog(null)}
        onSave={save}
      />
    </Layout>
  );
}

function ComplaintDialog({ dialog, operators, onClose, onSave }) {
  const { t } = useT();
  const [f, setF] = useState(dialog);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("complaints.file")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("complaints.complaintTitle")}</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="complaint-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("complaints.description")}</Label>
            <Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="complaint-description-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("complaints.about")}</Label>
            <Select value={f.about_operator_id || NONE} onValueChange={(v) => set("about_operator_id", v === NONE ? null : v)}>
              <SelectTrigger data-testid="complaint-about-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("complaints.none")}</SelectItem>
                {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(f)} data-testid="complaint-save-button">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
